/**
 * MobileDeals — Deals · Coupons · Surplus browser (mobile shell).
 *
 * Every dropdown is a bottom-sheet picker (matching the mockup):
 *   • Deals Type  — 18 taxonomy entries from dealsTaxonomy
 *   • Vendor      — async, queries GET /api/deals/vendors
 *   • Country     — full country list
 *   • State       — 50 US states + territories
 *   • City        — async, queries GET /api/deals/cities?state=…
 *   • Zip         — plain text
 *
 * "+ Add/Suggest Deals/Coupons/Surplus" jumps to /m/add-deal which
 * posts to POST /api/deals and persists to the `fp_deals` collection.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, Tag as TagIcon } from "lucide-react";
import { CATEGORY_TILES, CategoryTile } from "./MobileHome";
import MobileLocationBar from "./MobileLocationBar";
import BottomSheetPicker from "./BottomSheetPicker";
import { DEAL_TYPES } from "./dealsTaxonomy";
import { fetchCountries, fetchStates, fetchCities } from "./geoPickers";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#9CCA45";

const TABS = [
  { key: "offer",   label: "Deals/Offers" },
  { key: "coupon",  label: "Coupons" },
  { key: "surplus", label: "Surplus Deals" },
];

function Opener({ value, placeholder, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center justify-between border border-gray-100 rounded-2xl bg-white shadow-sm px-4 py-3.5 text-left"
    >
      <span className={`text-base font-medium ${value ? "text-gray-900" : "text-gray-500"}`}>
        {value || placeholder}
      </span>
      <span className="opacity-40">▾</span>
    </button>
  );
}

export default function MobileDeals() {
  const nav = useNavigate();
  const [tab, setTab] = useState("offer");
  const [q, setQ] = useState("");
  const [dealType, setDealType] = useState("");
  const [vendorSlug, setVendorSlug] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [country, setCountry] = useState("United States");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sheet, setSheet] = useState(null);

  const runSearch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/deals`, {
        params: {
          kind: tab,
          q: q || undefined,
          deal_type: dealType || undefined,
          vendor: vendorSlug || undefined,
          state: state || undefined,
          city: city || undefined,
          zip: zip || undefined,
          limit: 30,
        },
      });
      setItems(res.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runSearch(); /* eslint-disable-line */ }, [tab]);

  return (
    <div data-testid="mobile-deals-page">
      {/* 4 category tiles with Deals highlighted */}
      <div
        className="grid grid-cols-4 gap-2 px-3 pt-3 pb-4"
        style={{ background: "linear-gradient(180deg,#B5DA5E 0%,#CBEB90 100%)" }}
      >
        {CATEGORY_TILES.map((t) => (
          <CategoryTile key={t.key} tile={t} active={t.key === "deals"} />
        ))}
      </div>

      {/* Location */}
      <div className="flex items-center justify-center mt-4 mb-4">
        <MobileLocationBar testid="deals-location" />
      </div>

      {/* Sub-tabs */}
      <div className="px-4 flex gap-2" data-testid="deals-subtabs">
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`deals-tab-${t.key}`}
              className="px-5 py-2.5 rounded-full text-sm font-semibold flex-1"
              style={{
                background: active ? FP_PURPLE : "#fff",
                color: active ? "#fff" : "#1c1c1c",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Search row */}
      <div className="px-4 mt-4">
        <div className="flex items-center gap-2 bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3">
          <Search className="w-4 h-4 opacity-60" />
          <input
            data-testid="deals-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="Search product / vendor"
            className="flex-1 min-w-0 outline-none text-sm bg-transparent"
          />
          <button
            data-testid="deals-search-button"
            onClick={runSearch}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold"
            style={{ background: FP_PURPLE }}
          >
            Search
          </button>
        </div>
      </div>

      {/* Filter grid — bottom-sheet openers */}
      <div className="px-4 mt-4 grid grid-cols-2 gap-3">
        <Opener value={dealType} placeholder="Select Deals Type" onClick={() => setSheet("type")}   testId="deals-filter-type" />
        <Opener value={vendorName} placeholder="Select Vendor"  onClick={() => setSheet("vendor")} testId="deals-filter-vendor" />
        <Opener value={country}   placeholder="Select Country"  onClick={() => setSheet("country")} testId="deals-filter-country" />
        <Opener value={state}     placeholder="Select State"    onClick={() => setSheet("state")}  testId="deals-filter-state" />
        <Opener value={city}      placeholder="Select City"     onClick={() => setSheet("city")}   testId="deals-filter-city" />
        <label className="relative flex items-center rounded-2xl bg-white shadow-sm border border-gray-100">
          <input
            data-testid="deals-filter-zip"
            type="text"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="Enter Zip/Pin code"
            className="w-full bg-transparent px-4 py-3.5 text-base font-medium outline-none placeholder-gray-500"
          />
        </label>
      </div>

      {/* Add/Suggest CTA */}
      <div className="px-4 mt-5">
        <button
          onClick={() => nav("/m/add-deal")}
          data-testid="deals-add-suggest-btn"
          className="w-full py-3.5 rounded-2xl text-white text-base font-semibold flex items-center justify-center gap-2 shadow"
          style={{ background: FP_PURPLE }}
        >
          <Plus className="w-5 h-5" /> Add/Suggest Deals/Coupons/Surplus
        </button>
      </div>

      {/* Results */}
      <div className="px-4 mt-6 pb-16" data-testid="deals-results">
        {loading ? (
          <div className="text-sm opacity-60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm opacity-60">
            <TagIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
            No {TABS.find((t) => t.key === tab)?.label.toLowerCase()} yet in your area — be the first to suggest one!
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((d) => (
              <li key={d.slug} className="bg-white rounded-2xl border p-4 shadow-sm" data-testid={`deals-item-${d.slug}`}>
                <div className="text-sm font-bold">{d.title}</div>
                {d.vendor_name && <div className="text-xs opacity-70">{d.vendor_name}</div>}
                {d.description && <div className="text-sm mt-2">{d.description}</div>}
                {(d.discount_pct || d.discount_amount) && (
                  <div className="mt-2 inline-block text-xs font-bold rounded-md px-2 py-1 text-white" style={{ background: FP_PURPLE }}>
                    {d.discount_pct ? `${d.discount_pct}% off` : `$${d.discount_amount} off`}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bottom sheets */}
      <BottomSheetPicker
        open={sheet === "type"}
        title="Select Deals Type"
        options={DEAL_TYPES}
        onSelect={(v) => setDealType(v)}
        onClose={() => setSheet(null)}
        testIdPrefix="deals-type"
      />
      <BottomSheetPicker
        open={sheet === "country"}
        title="Select Country"
        fetchOptions={fetchCountries}
        onSelect={(v) => setCountry(v)}
        onClose={() => setSheet(null)}
        testIdPrefix="deals-country"
      />
      <BottomSheetPicker
        open={sheet === "state"}
        title="Select State"
        fetchOptions={(query) => fetchStates(country, query)}
        onSelect={(v) => { setState(v); setCity(""); }}
        onClose={() => setSheet(null)}
        testIdPrefix="deals-state"
      />
      <BottomSheetPicker
        open={sheet === "city"}
        title="Select City"
        fetchOptions={(query) => fetchCities(country, state, query)}
        onSelect={(v) => setCity(v)}
        onClose={() => setSheet(null)}
        testIdPrefix="deals-city"
      />
      <BottomSheetPicker
        open={sheet === "vendor"}
        title="Select Vendor"
        fetchOptions={async (query) => {
          const res = await axios.get(`${API}/api/deals/vendors`, {
            params: { q: query || undefined, limit: 30 },
          });
          return (res.data.items || []).map((v) => ({
            value: v.slug,
            label: v.name,
            subtitle: [v.city, v.state].filter(Boolean).join(", "),
          }));
        }}
        onSelect={(slug, name) => { setVendorSlug(slug); setVendorName(name); }}
        onClose={() => setSheet(null)}
        testIdPrefix="deals-vendor"
      />
    </div>
  );
}
